import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { queryKeys } from "@/lib/queries/query-keys";

export interface QuoteData {
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  clientName: string;
  clientAddress: string;
  clientPhone: string;
  clientEmail: string;
  quoteNumber: string;
  quoteDate: string;
  validityDate: string;
  taxRate: string;
  notes: string;
  lineItems: Array<{
    id: string;
    description: string;
    quantity: string;
    unit: string;
    unitPrice: string;
  }>;
}

interface EmailQuoteRequest {
  clientEmail: string;
  quoteData: QuoteData;
  subtotal: number;
  taxAmount: number;
  total: number;
}

interface EmailQuoteResponse {
  success: boolean;
  message: string;
}

/**
 * Custom hook to send quote via email
 * Uses React Query mutation for better state management
 */
export function useEmailQuote() {
  return useMutation({
    mutationKey: queryKeys.quotes.email,
    mutationFn: async (data: EmailQuoteRequest) => {
      const response = await fetch("/api/quotes/email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { error?: string };
        throw new Error(errorData.error || "Failed to send quote");
      }

      return response.json() as Promise<EmailQuoteResponse>;
    },
    onSuccess: (data) => {
      toast.success(data.message || "Quote sent successfully!");
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Failed to send quote";
      toast.error(message);
    },
  });
}
